import { Checkbox, Form, Grid, Header, Input, InputMask, SectionItem } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { ModalConfirm } from '@/components/Autre';
import { MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import type { ErrorFieldType, TresorerieType } from '@/types';
import { PosteComptableFormHandler, RecouvrementTresorerie } from '../../PosteComptable';

interface PosteComptableProps {
  identifiant?: string | number;
  errors?: ErrorFieldType[];
  onChange?: (e: any, shouldChangeStatus?: boolean) => void;
  tresorerie?: TresorerieType;
  depotFondTresor?: boolean;
  ribDepot?: string;
  intituleCompteDepot?: string;
  protocoleImpaye?: string;
  comptabilite?: string;
  seuilRecouvrement?: string | number;
}

const PosteComptable = (props: PosteComptableProps) => {
  const {
    identifiant,
    errors,
    tresorerie,
    depotFondTresor,
    ribDepot,
    intituleCompteDepot,
    protocoleImpaye,
    comptabilite,
    seuilRecouvrement,
    onChange,
  } = props;

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { regieModesReglement } = React.useContext(ModeReglementContext);

  const handleIbanChange = React.useCallback(
    (event) => {
      if (onChange) {
        onChange({
          identifiant,
          // à chaque modif, on en créera un nouveau
          intituleCompteDepot,
          ribDepot: _.toUpper(_.trim(_.join(_.split(event.target.value, ' '), ''))),
        });
      }
    },
    [identifiant, onChange, intituleCompteDepot],
  );

  const handleTitulaireChange = React.useCallback(
    (event, data) => {
      const { value } = data;

      if (onChange) {
        onChange({
          identifiant,
          // à chaque modif, on en créera un nouveau
          ribDepot,
          intituleCompteDepot: value,
        });
      }
    },
    [identifiant, onChange, ribDepot],
  );

  const handleTresorerieChange = React.useCallback(
    (treso, shouldChangeStatus = true) => {
      if (onChange) {
        onChange(
          {
            identifiant,
            tresorerie: treso,
          },
          shouldChangeStatus,
        );
      }
    },
    [identifiant, onChange],
  );

  const handleDFTChange = React.useCallback(
    (e, { checked }) => {
      if (onChange) {
        if (!checked) {
          if (_.some(regieModesReglement, { code: MODES_REGLEMENTS.PRELEVEMENT })) {
            // On doit désactiver le mode de règlement prélèvement
            // si on décoche le DFT
            setConfirmOpen(true);
          } else {
            onChange({
              identifiant,
              depotFondTresor: checked,
              ribDepot: '',
              intituleCompteDepot: '',
            });
          }
        } else {
          onChange({
            identifiant,
            depotFondTresor: checked,
          });
        }
      }
    },
    [identifiant, regieModesReglement, onChange],
  );

  const handleDisableDFTConfirm = React.useCallback(() => {
    setConfirmOpen(false);

    // On désactive le DFT
    onChange?.({
      identifiant,
      depotFondTresor: false,
      intituleCompteDepot: '',
      ribDepot: '',
      modesReglements: _.reject(regieModesReglement, { code: MODES_REGLEMENTS.PRELEVEMENT }),
    });
  }, [identifiant, onChange, regieModesReglement]);

  return (
    <>
      <SectionItem
        single
        compact
        titleRenderer={() => (
          <Header size="small" style={{ margin: 0 }}>
            Poste comptable
            <span style={{ color: '#db2828', marginLeft: '0.2em' }}>*</span>
          </Header>
        )}
      >
        {/* id utilisé par le gestionnaire d'erreurs pour défiler jusqu'au poste comptable */}
        <div id="tresorerie">
          <PosteComptableFormHandler
            tresorerie={tresorerie}
            error={_.some(errors, { id: 'tresorerie' })}
            onChange={handleTresorerieChange}
          />
        </div>
      </SectionItem>
      <SectionItem title="Dépôts de Fonds au Trésor" single compact>
        <Grid as={Form} stackable>
          <Grid.Row>
            <Grid.Column>
              <Checkbox
                data-testid="poste-comptable-checkbox-dft"
                toggle
                label="La régie possède un compte de Dépôts de Fonds au Trésor (DFT)"
                checked={depotFondTresor || false}
                onChange={handleDFTChange}
              />
              {confirmOpen && (
                <ModalConfirm
                  size="tiny"
                  header="Désactivation du mode de règlement Prélèvement"
                  content={
                    <Header as="h4">
                      <Header.Content>
                        <Header.Subheader>
                          Le mode de règlement <b>Prélèvement</b> sera désactivé lorsque le Dépôts
                          de Fonds au Trésor sera désactivé.
                        </Header.Subheader>
                        <br />
                        Souhaitez-vous poursuivre ?
                      </Header.Content>
                    </Header>
                  }
                  onConfirm={handleDisableDFTConfirm}
                  onCancel={() => {
                    setConfirmOpen(false);
                  }}
                />
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={6}>
              <Form.Field
                id="intituleCompteDepot"
                input={{ 'data-testid': 'poste-comptable-input-titulaire-dft' }}
                required={depotFondTresor}
                label="Intitulé du compte"
                name="titulaire"
                value={intituleCompteDepot}
                // @ts-expect-error
                control={Input}
                fluid
                disabled={!depotFondTresor}
                onChange={handleTitulaireChange}
                error={_.some(errors, { id: 'intituleCompteDepot' })}
              />
            </Grid.Column>
            <Grid.Column width={3} />
            <Grid.Column width={6} style={{ paddingLeft: 0 }}>
              <Form.Field
                // @ts-expect-error
                id="ribDepot"
                required={depotFondTresor}
                disabled={!depotFondTresor}
                error={_.some(errors, { id: 'ribDepot' })}
              >
                <label>IBAN</label>
                <InputMask
                  data-testid="ribform-input-iban"
                  // @ts-expect-error pas typé
                  name="iban"
                  mask="aa99 9999 9999 99** **** **** *99"
                  placeholder="FRXX XXXX XXXX XXXX XXXX XXXX XXX"
                  maskPlaceholder=" "
                  value={ribDepot as string} // todo
                  // disabled={!depotFondTresor}
                  onChange={handleIbanChange}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={3} textAlign="center" />
          </Grid.Row>
        </Grid>
      </SectionItem>
      <SectionItem title="Recouvrement des impayés" single compact>
        <RecouvrementTresorerie
          identifiant={identifiant}
          errors={errors}
          protocoleImpaye={protocoleImpaye}
          comptabilite={comptabilite}
          seuilRecouvrement={seuilRecouvrement}
          onChange={onChange}
        />
      </SectionItem>
    </>
  );
};

export default PosteComptable;
